import React, { useState, useMemo, useCallback } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { Receipt, Play, CaretDown, CaretRight, Buildings, ComputerTower, Megaphone, Bank, Scales, GraduationCap, Vault, Pencil } from '@phosphor-icons/react';
import { formatMoney } from '../utils/formatMoney';

// SaaS average defaults (monthly amounts in $)
const DEFAULT_CATEGORIES = [
  {
    id: 'admin',
    icon: 'Buildings',
    color: 'indigo',
    items: [
      { id: 'salaries', fr: 'Salaires (admin, support, comptabilite)', en: 'Salaries (admin, support, accounting)', value: 3000, type: 'flat' },
      { id: 'office', fr: 'Loyer / Coworking', en: 'Rent / Coworking', value: 500, type: 'flat' },
      { id: 'saas_tools', fr: 'Outils SaaS (CRM, helpdesk)', en: 'SaaS Tools (CRM, helpdesk)', value: 200, type: 'flat' },
    ]
  },
  {
    id: 'tech',
    icon: 'ComputerTower',
    color: 'cyan',
    items: [
      { id: 'hosting', fr: 'Hebergement Cloud / Serveurs', en: 'Cloud Hosting / Servers', value: 300, type: 'flat' },
      { id: 'dev', fr: 'Developpement & Maintenance', en: 'Development & Maintenance', value: 2000, type: 'flat' },
      { id: 'security', fr: 'Securite & Backups', en: 'Security & Backups', value: 100, type: 'flat' },
    ]
  },
  {
    id: 'marketing',
    icon: 'Megaphone',
    color: 'orange',
    section: 'marketing',
    items: [
      { id: 'ads', fr: 'Publicite digitale (Ads)', en: 'Digital Advertising (Ads)', value: 1500, type: 'flat' },
      { id: 'content', fr: 'Creation contenu & SEO', en: 'Content Creation & SEO', value: 500, type: 'flat' },
      { id: 'events', fr: 'Evenements / Webinaires', en: 'Events / Webinars', value: 300, type: 'flat' },
      { id: 'promo', fr: 'Materiel promotionnel', en: 'Promotional Material', value: 200, type: 'flat' },
    ]
  },
  {
    id: 'financial',
    icon: 'Bank',
    color: 'amber',
    items: [
      { id: 'payment_gateway', fr: 'Passerelle paiement', en: 'Payment Gateway', value: 2.9, type: 'percent' },
      { id: 'banking', fr: 'Frais bancaires & transferts', en: 'Banking & Transfer Fees', value: 50, type: 'flat' },
      { id: 'chargebacks', fr: 'Provisions chargebacks', en: 'Chargeback Provisions', value: 100, type: 'flat' },
      { id: 'currency', fr: 'Conversion de devises', en: 'Currency Conversion', value: 50, type: 'flat' },
    ]
  },
  {
    id: 'legal',
    icon: 'Scales',
    color: 'purple',
    items: [
      { id: 'lawyer', fr: 'Avocat / Conseiller juridique', en: 'Lawyer / Legal Counsel', value: 500, type: 'flat' },
      { id: 'insurance', fr: 'Assurances professionnelles', en: 'Professional Insurance', value: 200, type: 'flat' },
      { id: 'audit', fr: 'Audit comptable', en: 'Accounting Audit', value: 200, type: 'flat' },
    ]
  },
  {
    id: 'training',
    icon: 'GraduationCap',
    color: 'emerald',
    items: [
      { id: 'leader_training', fr: 'Formation leaders / affilies', en: 'Leader / Affiliate Training', value: 300, type: 'flat' },
      { id: 'support', fr: 'Support client (chat, email)', en: 'Customer Support (chat, email)', value: 500, type: 'flat' },
      { id: 'elearning', fr: 'Plateforme e-learning', en: 'E-learning Platform', value: 100, type: 'flat' },
    ]
  },
  {
    id: 'reserves',
    icon: 'Vault',
    color: 'rose',
    items: [
      { id: 'working_capital', fr: 'Fond de roulement', en: 'Working Capital', value: 500, type: 'flat' },
      { id: 'taxes', fr: 'Provision impots', en: 'Tax Provision', value: 15, type: 'percent_profit' },
      { id: 'contingency', fr: 'Imprevus', en: 'Contingency', value: 300, type: 'flat' },
    ]
  },
];

const CATEGORY_LABELS = {
  admin: { fr: 'Administration & Gestion', en: 'Administration & Management' },
  tech: { fr: 'Technologie', en: 'Technology' },
  marketing: { fr: 'Ventes & Marketing', en: 'Sales & Marketing' },
  financial: { fr: 'Frais financiers', en: 'Financial Costs' },
  legal: { fr: 'Juridique & Conformite', en: 'Legal & Compliance' },
  training: { fr: 'Formation & Support', en: 'Training & Support' },
  reserves: { fr: 'Reserves & Provisions', en: 'Reserves & Provisions' },
};

const ICON_MAP = { Buildings, ComputerTower, Megaphone, Bank, Scales, GraduationCap, Vault };

const OrbitExpensesPage = () => {
  const { language } = useLanguage();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [categories, setCategories] = useState(() =>
    DEFAULT_CATEGORIES.map(cat => ({
      ...cat,
      items: cat.items.map(item => ({ ...item })),
    }))
  );
  const [expandedCat, setExpandedCat] = useState('admin');
  const [estRevenue, setEstRevenue] = useState(() => {
    try {
      const sim = JSON.parse(localStorage.getItem('orbit_simulator_data'));
      if (sim?.annuals?.[0]) return Math.round(sim.annuals[0].totalRev / 12);
    } catch (e) { console.error('Load expenses:', e); }
    return 5000;
  });
  const [results, setResults] = useState(null);
  const [calculated, setCalculated] = useState(false);

  const updateItem = (catIdx, itemIdx, newValue) => {
    setCategories(prev => {
      const updated = prev.map(c => ({ ...c, items: c.items.map(i => ({ ...i })) }));
      updated[catIdx].items[itemIdx] = { ...updated[catIdx].items[itemIdx], value: Number(newValue) };
      return updated;
    });
  };

  const calculate = useCallback(() => {
    const catResults = categories.map(cat => {
      const label = CATEGORY_LABELS[cat.id];
      let monthlyFlat = 0;
      let monthlyVariable = 0;
      const itemResults = cat.items.map(item => {
        let monthly;
        if (item.type === 'flat') {
          monthly = item.value;
          monthlyFlat += monthly;
        } else if (item.type === 'percent') {
          monthly = estRevenue * item.value / 100;
          monthlyVariable += monthly;
        } else if (item.type === 'percent_profit') {
          // Estimate: profit = revenue * 30% (rough SaaS margin for calculation)
          monthly = estRevenue * 0.3 * item.value / 100;
          monthlyVariable += monthly;
        }
        return { ...item, monthly, annual: monthly * 12 };
      });
      const monthlyTotal = monthlyFlat + monthlyVariable;
      return {
        id: cat.id,
        label,
        section: cat.section || 'operating',
        color: cat.color,
        items: itemResults,
        monthlyTotal,
        annualTotal: monthlyTotal * 12,
      };
    });

    const operatingCats = catResults.filter(c => c.section === 'operating');
    const marketingCats = catResults.filter(c => c.section === 'marketing');

    const totalMonthlyOp = operatingCats.reduce((s, c) => s + c.monthlyTotal, 0);
    const totalMonthlyMkt = marketingCats.reduce((s, c) => s + c.monthlyTotal, 0);
    const totalMonthly = totalMonthlyOp + totalMonthlyMkt;

    const res = {
      categories: catResults,
      operatingCats,
      marketingCats,
      totalMonthlyOperating: totalMonthlyOp,
      totalMonthlyMarketing: totalMonthlyMkt,
      totalMonthly,
      totalAnnual: totalMonthly * 12,
      estRevenue,
    };

    setResults(res);
    setCalculated(true);

    // Save to localStorage for Cash Flow page
    try {
      localStorage.setItem('orbit_expenses_data', JSON.stringify({
        ...res,
        timestamp: new Date().toISOString(),
        rawCategories: categories.map(c => ({ id: c.id, section: c.section || 'operating', items: c.items })),
      }));
    } catch (e) { console.error('Save expenses:', e); }
  }, [categories, estRevenue]);

  const colorClasses = {
    indigo: 'border-indigo-500/20 text-indigo-400',
    cyan: 'border-cyan-500/20 text-cyan-400',
    orange: 'border-orange-500/20 text-orange-400',
    amber: 'border-amber-500/20 text-amber-400',
    purple: 'border-purple-500/20 text-purple-400',
    emerald: 'border-emerald-500/20 text-emerald-400',
    rose: 'border-rose-500/20 text-rose-400',
  };

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="orbit-expenses-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <Receipt className="w-8 h-8 text-amber-400" />
            {t('Postes de depenses', 'Expense Categories')}
          </h1>
          <p className="text-gray-300 text-sm mt-1">{t('Frais d\'exploitation mensuels avec valeurs par defaut SaaS', 'Monthly operating expenses with SaaS default values')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Expense Editor */}
          <div className="lg:col-span-1 space-y-3">

            {/* Estimated Revenue */}
            <div className="bg-[#111827]/80 border border-emerald-500/20 rounded-xl p-4">
              <label className="block text-xs text-gray-400 mb-1">{t('Revenu mensuel estime (pour calcul des %)', 'Estimated monthly revenue (for % calculation)')}</label>
              <div className="relative">
                <input type="number" min="0" value={estRevenue} onChange={e => setEstRevenue(Number(e.target.value))}
                  className="w-full px-3 py-2.5 bg-[#1E293B] border border-emerald-500/20 rounded-lg text-white text-sm pr-12"
                  data-testid="exp-est-revenue" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-400 text-xs font-medium">$/mo</span>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">{t('Utilise pour les postes en % (passerelle, impots)', 'Used for percentage-based items (gateway, taxes)')}</p>
            </div>

            {/* Category Accordions */}
            {categories.map((cat, catIdx) => {
              const isExpanded = expandedCat === cat.id;
              const Icon = ICON_MAP[cat.icon];
              const label = CATEGORY_LABELS[cat.id];
              const cc = colorClasses[cat.color] || colorClasses.indigo;

              return (
                <div key={cat.id} className={`bg-[#111827]/80 border ${cc.split(' ')[0]} rounded-xl overflow-hidden`}>
                  <button onClick={() => setExpandedCat(isExpanded ? null : cat.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                    data-testid={`exp-cat-${cat.id}`}>
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className={`w-4 h-4 ${cc.split(' ')[1]}`} />}
                      <span className="text-sm font-semibold text-white">{language === 'fr' ? label.fr : label.en}</span>
                      {cat.section === 'marketing' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 uppercase">S&M</span>
                      )}
                    </div>
                    {isExpanded ? <CaretDown className="w-4 h-4 text-gray-400" /> : <CaretRight className="w-4 h-4 text-gray-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-2.5">
                      {cat.items.map((item, itemIdx) => (
                        <div key={item.id} className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs flex-1 min-w-0 truncate" title={language === 'fr' ? item.fr : item.en}>
                            {language === 'fr' ? item.fr : item.en}
                          </span>
                          <div className="relative w-24 flex-shrink-0">
                            <input type="number" step={item.type === 'flat' ? '10' : '0.1'} min="0" value={item.value}
                              onChange={e => updateItem(catIdx, itemIdx, e.target.value)}
                              className="w-full px-2 py-1.5 bg-[#1E293B] border border-white/10 rounded text-white text-xs text-right pr-8"
                              data-testid={`exp-item-${item.id}`} />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px]">
                              {item.type === 'flat' ? '$/mo' : item.type === 'percent' ? '% rev' : '% profit'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* CALCULATE BUTTON */}
            <button onClick={calculate}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:from-amber-400 hover:to-orange-400 transition-all shadow-lg shadow-amber-500/25 flex items-center justify-center gap-2 text-lg"
              data-testid="exp-calculate-btn">
              <Play className="w-6 h-6" weight="fill" />
              {t('Calculer', 'Calculate')}
            </button>
          </div>

          {/* RIGHT: Results */}
          {calculated && results ? (
          <div className="lg:col-span-2 space-y-5">

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-xl p-4 text-center">
                <p className="text-[10px] text-gray-400 uppercase mb-1">{t('Depenses operationnelles', 'Operating Expenses')}</p>
                <p className="text-xl font-bold text-indigo-400">{formatMoney(results.totalMonthlyOperating, language, 0)}</p>
                <p className="text-[10px] text-gray-400">/{t('mois', 'mo')}</p>
              </div>
              <div className="bg-[#111827]/80 border border-orange-500/20 rounded-xl p-4 text-center">
                <p className="text-[10px] text-gray-400 uppercase mb-1">{t('Ventes & Marketing', 'Sales & Marketing')}</p>
                <p className="text-xl font-bold text-orange-400">{formatMoney(results.totalMonthlyMarketing, language, 0)}</p>
                <p className="text-[10px] text-gray-400">/{t('mois', 'mo')}</p>
              </div>
              <div className="bg-[#111827]/80 border border-red-500/20 rounded-xl p-4 text-center">
                <p className="text-[10px] text-gray-400 uppercase mb-1">{t('Total mensuel', 'Total Monthly')}</p>
                <p className="text-xl font-bold text-red-400">{formatMoney(results.totalMonthly, language, 0)}</p>
                <p className="text-[10px] text-gray-400">{formatMoney(results.totalAnnual, language, 0)} /{t('an', 'yr')}</p>
              </div>
            </div>

            {/* Detailed Breakdown per Category */}
            <div className="space-y-3">
              {/* Operating Section */}
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Depenses d\'exploitation', 'Operating Expenses')}</h3>
              {results.operatingCats.map(cat => (
                <div key={cat.id} className={`bg-[#111827]/80 border ${colorClasses[cat.color]?.split(' ')[0] || 'border-white/10'} rounded-xl p-4`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`text-sm font-semibold ${colorClasses[cat.color]?.split(' ')[1] || 'text-white'}`}>
                      {language === 'fr' ? cat.label.fr : cat.label.en}
                    </h4>
                    <div className="text-right">
                      <span className="text-white font-bold text-sm">{formatMoney(cat.monthlyTotal, language, 0)}</span>
                      <span className="text-gray-400 text-xs ml-1">/{t('mois', 'mo')}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {cat.items.map(item => (
                      <div key={item.id} className="flex justify-between text-xs">
                        <span className="text-gray-400">
                          {language === 'fr' ? item.fr : item.en}
                          {item.type !== 'flat' && (
                            <span className="ml-1 text-[10px] text-amber-400">({item.value}{item.type === 'percent' ? '% rev' : '% profit'})</span>
                          )}
                        </span>
                        <span className="text-gray-300">{formatMoney(item.monthly, language, 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Marketing Section */}
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-4">{t('Ventes & Marketing', 'Sales & Marketing')}</h3>
              {results.marketingCats.map(cat => (
                <div key={cat.id} className={`bg-[#111827]/80 border ${colorClasses[cat.color]?.split(' ')[0] || 'border-white/10'} rounded-xl p-4`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className={`text-sm font-semibold ${colorClasses[cat.color]?.split(' ')[1] || 'text-white'}`}>
                      {language === 'fr' ? cat.label.fr : cat.label.en}
                    </h4>
                    <div className="text-right">
                      <span className="text-white font-bold text-sm">{formatMoney(cat.monthlyTotal, language, 0)}</span>
                      <span className="text-gray-400 text-xs ml-1">/{t('mois', 'mo')}</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {cat.items.map(item => (
                      <div key={item.id} className="flex justify-between text-xs">
                        <span className="text-gray-400">{language === 'fr' ? item.fr : item.en}</span>
                        <span className="text-gray-300">{formatMoney(item.monthly, language, 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Grand Total */}
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-white font-semibold">{t('Total des depenses', 'Total Expenses')}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('Exploitation', 'Operating')}: {formatMoney(results.totalMonthlyOperating, language, 0)} + {t('Marketing', 'Marketing')}: {formatMoney(results.totalMonthlyMarketing, language, 0)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-red-400">{formatMoney(results.totalMonthly, language, 0)}<span className="text-sm text-gray-500 ml-1">/{t('mois', 'mo')}</span></p>
                  <p className="text-sm text-red-300 mt-0.5">{formatMoney(results.totalAnnual, language, 0)}<span className="text-xs text-gray-500 ml-1">/{t('an', 'yr')}</span></p>
                </div>
              </div>
            </div>

            {/* Info: Saved for Cash Flow */}
            <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 text-center">
              <p className="text-xs text-indigo-300">{t('Donnees sauvegardees automatiquement pour le Cash Flow Statement', 'Data automatically saved for the Cash Flow Statement')}</p>
            </div>
          </div>
          ) : (
          <div className="lg:col-span-2 flex items-center justify-center">
            <div className="text-center py-20 px-8">
              <Pencil className="w-20 h-20 text-amber-500/15 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">{t('Editez vos postes de depenses et cliquez sur', 'Edit your expense items and click')}</p>
              <p className="text-amber-400 font-bold text-2xl mt-2">{t('Calculer', 'Calculate')}</p>
              <p className="text-gray-400 text-sm mt-3">{t('Valeurs par defaut basees sur la moyenne du marche SaaS', 'Default values based on SaaS market averages')}</p>
            </div>
          </div>
          )}
        </div>
      </div>
    </OrbitLayout>
  );
};

export default OrbitExpensesPage;
